"use client"

import type React from "react"
import { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  FormGroup,
  Label,
  Input,
  Spinner,
  Badge,
  Table,
} from "reactstrap"
import { ExhibitorRegistrationService } from "../../../Service/exhibitor-registration-service"
import type { Car, CarRegistration } from "./types"
import { toast } from "react-toastify"
import { FaPlus, FaTrash, FaCar, FaEdit } from "react-icons/fa"
import Swal from "sweetalert2"

interface Props {
  participationId: number
  exhibitorName: string
}

const CarManagement: React.FC<Props> = ({ participationId, exhibitorName }) => {
  const [cars, setCars] = useState<Car[]>([])
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingCar, setEditingCar] = useState<Car | null>(null)
  const [form, setForm] = useState<CarRegistration>({
    plate_number: "",
    driver_name: "",
    driver_phone: "",
  })

  useEffect(() => {
    fetchCars()
  }, [participationId])

  const fetchCars = async () => {
    setLoading(true)
    try {
      const data = await ExhibitorRegistrationService.getParticipationCars(participationId)
      setCars(data)
    } catch (error) {
      toast.error("Failed to load cars")
    } finally {
      setLoading(false)
    }
  }

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setForm({
      ...form,
      [name]: value,
    })
  }

  const handleAddCar = () => {
    setEditingCar(null)
    resetForm()
    setModalOpen(true)
  }

  const handleEditCar = (car: Car) => {
    setEditingCar(car)
    setForm({
      plate_number: car.plate_number,
      driver_name: car.driver_name,
      driver_phone: car.driver_phone || "",
    })
    setModalOpen(true)
  }

  const handleSubmit = async () => {
    if (!form.plate_number.trim() || !form.driver_name.trim()) {
      toast.error("Plate number and driver name are required")
      return
    }

    setLoading(true)
    try {
      if (editingCar) {
        await ExhibitorRegistrationService.updateCar(editingCar.id, form)
        await Swal.fire({
          title: "Car Updated!",
          text: `Car "${form.plate_number}" has been successfully updated.`,
          icon: "success",
          confirmButtonColor: "#28a745",
        })
      } else {
        await ExhibitorRegistrationService.registerCar(participationId, form)
        await Swal.fire({
          title: "Car Registered!",
          text: `Car "${form.plate_number}" has been successfully registered.`,
          icon: "success",
          confirmButtonColor: "#28a745",
        })
      }

      setModalOpen(false)
      await fetchCars()
      resetForm()
      setEditingCar(null)
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.non_field_errors?.[0] ||
        error.response?.data?.message ||
        `Failed to ${editingCar ? "update" : "register"} car`

      await Swal.fire({
        title: `${editingCar ? "Update" : "Registration"} Failed`,
        text: errorMessage,
        icon: "error",
        confirmButtonColor: "#dc3545",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveCar = async (car: Car) => {
    const result = await Swal.fire({
      title: "Remove Car?",
      text: `This will permanently remove car "${car.plate_number}" from the list.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove permanently",
      reverseButtons: true,
    })

    if (!result.isConfirmed) return

    try {
      await ExhibitorRegistrationService.removeCar(car.id)
      await Swal.fire({
        title: "Car Removed!",
        text: `Car "${car.plate_number}" has been permanently removed.`,
        icon: "success",
        confirmButtonColor: "#28a745",
      })
      await fetchCars()
    } catch (error) {
      await Swal.fire({
        title: "Removal Failed",
        text: "Failed to remove car. Please try again.",
        icon: "error",
        confirmButtonColor: "#dc3545",
      })
    }
  }

  const handleCloseModal = () => {
    setModalOpen(false)
    setEditingCar(null)
    resetForm()
  }

  const resetForm = () => {
    setForm({
      plate_number: "",
      driver_name: "",
      driver_phone: "",
    })
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5>Car Management - {exhibitorName}</h5>
          <div>
            <Badge color="info" className="me-2">
              {cars.length} Cars Registered
            </Badge>
          </div>
        </div>

        <div className="mb-3">
          <Button color="success" onClick={handleAddCar}>
            <FaPlus className="me-1" /> Add Car
          </Button>
        </div>

        {loading && cars.length === 0 ? (
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        ) : (
          <Table responsive striped>
            <thead>
              <tr>
                <th>Plate Number</th>
                <th>Driver Name</th>
                <th>Driver Phone</th>
                <th>Registered</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cars.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No cars registered yet
                  </td>
                </tr>
              ) : (
                cars.map((car) => (
                  <tr key={car.id}>
                    <td>
                      <FaCar className="me-2 text-muted" />
                      {car.plate_number}
                    </td>
                    <td>{car.driver_name}</td>
                    <td>{car.driver_phone || "-"}</td>
                    <td>{new Date(car.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button color="info" size="sm" onClick={() => handleEditCar(car)} title="Edit car">
                          <FaEdit size={12} />
                        </Button>
                        <Button color="danger" size="sm" onClick={() => handleRemoveCar(car)} title="Remove car">
                          <FaTrash size={12} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}

        <Modal isOpen={modalOpen} toggle={handleCloseModal}>
          <ModalHeader toggle={handleCloseModal}>{editingCar ? "Edit Car" : "Register Car"}</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="plate_number">Plate Number *</Label>
              <Input
                name="plate_number"
                value={form.plate_number}
                onChange={handleFormChange}
                placeholder="e.g., ABC-123"
                style={{ textTransform: "uppercase" }}
              />
            </FormGroup>
            <FormGroup>
              <Label for="driver_name">Driver Name *</Label>
              <Input
                name="driver_name"
                value={form.driver_name}
                onChange={handleFormChange}
                placeholder="Full name of the driver"
              />
            </FormGroup>
            <FormGroup>
              <Label for="driver_phone">Driver Phone</Label>
              <Input
                name="driver_phone"
                value={form.driver_phone}
                onChange={handleFormChange}
                placeholder="Driver's phone number"
              />
            </FormGroup>
            <div className="text-end mt-4">
              <Button color="secondary" onClick={handleCloseModal} className="me-2">
                Cancel
              </Button>
              <Button color="primary" onClick={handleSubmit} disabled={loading}>
                {loading ? <Spinner size="sm" /> : editingCar ? "Update Car" : "Register Car"}
              </Button>
            </div>
          </ModalBody>
        </Modal>
      </CardBody>
    </Card>
  )
}

export default CarManagement
